import React, { useCallback, useEffect, useState } from 'react'

import {
  Box,
  DisplayPosition,
  ModalClose,
  ModalFooter,
  ModalHeader,
  Overflow,
  ZIndex,
} from '@gousto-internal/citrus-react'
import { useDispatch, useSelector } from 'react-redux'

import { FlexModalOverlay } from '@features/recipe-tile'
import type { RecipeQueryResponse } from '@library/api-menu-experience'

import type { RecipeUUID } from 'reducers/menu-recipes/recipes'
import { menuRecipeDetailVisibilityChange } from 'routes/Menu/actions/menuRecipeDetails'
import { RecipeDescription } from 'routes/Menu/components/Detail/Description'
import { DetailError } from 'routes/Menu/components/Detail/DetailError'
import { RecipeImage } from 'routes/Menu/components/Detail/Image'
import { SectionPanel } from 'routes/Menu/components/Detail/SectionPanel'
import { Title } from 'routes/Menu/components/Detail/Title'
import { getNumPortions } from 'selectors/basket'

import { RecipeAllergens } from './Allergens/RecipeAllergens'
import { RecipeAttributes } from './Attributes/RecipeAttributes'
import { RecipeQuickStats } from './Attributes/RecipeQuickStats'
import { RecipeEquipment } from './Equipment/RecipeEquipment'
import { FlexPortionFooter } from './FlexPortionFooter'
import { RecipeIngredients } from './Ingredients/RecipeIngredients'
import { RecipeNutrition } from './Nutrition/RecipeNutrition'
import { RecipeOptions } from './Options/RecipeOptions'

type DetailContentProps = {
  uuid: RecipeUUID
  title: string
  error: boolean
  loading: boolean
  recipe: RecipeQueryResponse | null
  position?: string
  reload: () => void
  onClose: () => void
  onSelectRecipe?: (recipeId: RecipeUUID) => void
  onRecipeDetailsClose?: () => void
  numPortions?: number
  onUpdatePortions: (numPortions: number) => void
  initialFlexPortionEditorOpen?: boolean
}

export const DetailContent = ({
  uuid,
  title,
  error,
  loading,
  recipe,
  reload,
  onClose,
  onSelectRecipe,
  onRecipeDetailsClose,
  numPortions: selectedNumPortions,
  onUpdatePortions,
  position,
  initialFlexPortionEditorOpen = false,
}: DetailContentProps) => {
  const boxNumPortions = useSelector(getNumPortions)
  const numPortions = selectedNumPortions ?? boxNumPortions
  const dispatch = useDispatch()
  const [isFlexPortionEditorOpen, setIsFlexPortionEditorOpen] = useState(
    initialFlexPortionEditorOpen,
  )
  const isFlexPortionEditorActive = !error && isFlexPortionEditorOpen
  const closeFlexPortionEditor = useCallback(() => setIsFlexPortionEditorOpen(false), [])

  useEffect(() => {
    setIsFlexPortionEditorOpen(initialFlexPortionEditorOpen)
  }, [initialFlexPortionEditorOpen, uuid])

  return (
    <>
      <ModalClose onClose={onClose} />
      <ModalHeader>
        <Title data-testing="recipeDetailTitle">{title}</Title>
      </ModalHeader>
      <Box
        flexGrow={1}
        minHeight="0"
        overflowY={isFlexPortionEditorActive ? Overflow.Hidden : Overflow.Scroll}
        paddingH={[4, 6]}
        paddingV={[4, 6]}
      >
        <Box>
          <RecipeImage
            images={{ experience: recipe?.images }}
            title={title}
            banner={recipe?.tagGroups?.banner}
          />
          <SectionPanel>
            <RecipeOptions
              customisations={recipe?.customisations}
              selectedRecipeId={uuid}
              onSelectRecipe={
                onSelectRecipe ||
                ((recipeId) => dispatch(menuRecipeDetailVisibilityChange(recipeId)))
              }
            />
            <RecipeQuickStats tagGroups={recipe?.tagGroups} displayType={recipe?.displayType} />
            <RecipeDescription
              description={recipe?.description}
              packagingDescriptionHTML={recipe?.packagingDescriptionHTML}
              loading={loading}
            />
            <RecipeAttributes tagGroups={recipe?.tagGroups} loading={loading} />
          </SectionPanel>
          {error ? (
            <DetailError reload={reload} />
          ) : (
            <>
              <RecipeIngredients ingredients={recipe?.ingredients} loading={loading} />
              <RecipeEquipment equipment={recipe?.equipment} loading={loading} onClose={onClose} />
              <RecipeNutrition
                nutrition={recipe?.nutrition}
                numPortions={numPortions}
                tagGroups={recipe?.tagGroups}
                loading={loading}
              />
              <RecipeAllergens
                ingredients={recipe?.ingredients}
                allergens={recipe?.allergens}
                loading={loading}
              />
            </>
          )}
        </Box>
      </Box>
      {isFlexPortionEditorActive && <FlexModalOverlay onClick={closeFlexPortionEditor} />}
      {!error && (
        <Box position={DisplayPosition.Relative} zIndex={ZIndex.Top}>
          <ModalFooter>
            <FlexPortionFooter
              id={uuid}
              surchargeLabel={recipe?.pricing?.perPortionSurcharge}
              portions={recipe?.portions}
              isNegativeSurcharge={Boolean(recipe?.pricing?.isNegative)}
              isRecipeAvailable={recipe?.isAvailable ?? false}
              position={position}
              onRecipeDetailsClose={onRecipeDetailsClose ?? onClose}
              // Flex portion updates are currently only wired from the DetailOverlay flow.
              // MenuExperienceRecipeDetailOverlay also shares this component, so callers
              // without local portion state keep the previous no-op update behaviour.
              numPortions={numPortions}
              onUpdatePortions={onUpdatePortions}
              mode={isFlexPortionEditorActive ? 'editor' : 'summary'}
              onEditPortions={() => setIsFlexPortionEditorOpen(true)}
              onCloseEditor={closeFlexPortionEditor}
            />
          </ModalFooter>
        </Box>
      )}
    </>
  )
}
